// Coordinate strings meant for Unity must ALWAYS use '.' as the decimal separator.
// Never use toLocaleString / Intl here: under sv-SE the browser writes "-178,2", which Unity won't parse.

/** Invariant fixed-point formatting with trailing zeros trimmed ("-178.2", "13", "0.05"). */
export function num(v: number, decimals = 2): string {
  if (!Number.isFinite(v)) return '0';
  const s = v.toFixed(decimals);
  const trimmed = s.includes('.') ? s.replace(/\.?0+$/, '') : s;
  return trimmed === '-0' ? '0' : trimmed;
}

/** The format Unity's Inspector accepts when pasting a Vector3: "Vector3(-178.2,13.2,-357.9)". */
export function unityInspectorVector3(x: number, y: number, z: number): string {
  return `Vector3(${num(x)},${num(y)},${num(z)})`;
}

/** C# source form: "new Vector3(-178.2f, 13.2f, -357.9f)". */
export function csharpVector3(x: number, y: number, z: number): string {
  return `new Vector3(${num(x)}f, ${num(y)}f, ${num(z)}f)`;
}

export function pointsJson(points: [number, number][]): string {
  return '[' + points.map(([x, z]) => `[${num(x)},${num(z)}]`).join(',') + ']';
}

/** Human readout for the status bar, always invariant as well so it can be copied. */
export function readoutXZ(x: number, z: number): string {
  return `X ${num(x, 1)}  Z ${num(z, 1)}`;
}

/**
 * Parses pasted coordinates: "Vector3(-178.2, 13.2, -357.9)", "new Vector3(1f,2f,3f)",
 * "(-178.2, 13.2, -357.9)", "-178.2, -357.9" (x, z) or "x:-178 z:-357".
 * A 3-component value is read as (x, y, z). Returns null when nothing usable was found.
 */
export function parseCoordinates(text: string): { x: number; y: number | null; z: number } | null {
  const t = text.trim();
  const labelled =
    /x\s*[:=]\s*(-?\d+(?:\.\d+)?)[\s,;]+(?:y\s*[:=]\s*(-?\d+(?:\.\d+)?)[\s,;]+)?z\s*[:=]\s*(-?\d+(?:\.\d+)?)/i.exec(
      t,
    );
  if (labelled) {
    return {
      x: Number(labelled[1]),
      y: labelled[2] !== undefined ? Number(labelled[2]) : null,
      z: Number(labelled[3]),
    };
  }
  const nums = [...t.replace(/(\d)f\b/g, '$1').matchAll(/-?\d+(?:\.\d+)?/g)].map((m) => Number(m[0]));
  // Ignore a leading "3" from "Vector3(".
  const cleaned = /vector3/i.test(t) ? nums.slice(1) : nums;
  if (cleaned.length === 3) return { x: cleaned[0], y: cleaned[1], z: cleaned[2] };
  if (cleaned.length === 2) return { x: cleaned[0], y: null, z: cleaned[1] };
  return null;
}

/** Rounds every coordinate to 0.01 m, the precision the server stores. */
export function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
